/** @jsx jsx */
import { Button, Inline, jsx, Separator, Stack, Text } from '@front10/base-elements';
import { useMobileFilter, usePopupFilter } from '../../../components/lab/FlexibleFilterContainer';
import { PeopleCounter, PeopleInfo } from '../../lab/PeopleCounter';
import {
  MobileBody,
  MobileFilterDialog,
  MobileFooter,
  MobileHeader
} from '../../lab/MobileFilterView';
import { Listbox } from '../../lab/Listbox';
import { FilterButton } from '../../lab/FilterButton';
import { Dropdown } from '../../lab/Dropdown';
import { count, SelectProps } from '../../lab/utils';
import { usePlural, useTranslation, useIsMobileView } from '@front10/utils';
import produce from 'immer';
import nanoid from 'nanoid';
import React, { Fragment, useState } from 'react';
import { useEffectReducer } from 'use-effect-reducer';
import { PassengersProps, RoomsProps } from './bookingBusinessRules';
import { BookingComponentsConfigurations } from './bookingComponentsConfigurations';
import { PanelSingleType } from './BookingPanel';
import { evaluateRules } from '../../lab/PeopleCounter/rulesParser';
import Alert from '../../lab/Alert';
import { KEY_ENTER, KEY_SPACE } from '../../lab/DateRangePicker/helper';
import VisuallyHidden from '@reach/visually-hidden';

export type RoomInfo = { key: string; count: number[]; ages: number[][] };
export type RoomsInfo = { rooms: RoomInfo[]; travelClass?: string };

interface Props {
  /**
   * Value of the guest selector
   */
  defaultValue?: Partial<RoomsInfo>;
  title?: string;
  rooms: Omit<RoomsProps, 'defaultSelection'>;
  activateRouteOption?: boolean;
  trackTravelersChange?: (value: RoomInfo[]) => void;
  onChange?: (newValue: RoomsInfo) => void;
  travelClasses?: Omit<SelectProps, 'defaultValue'>;
  travelClassConfig?: BookingComponentsConfigurations['travelClassSelector'];
  passengersRules?: Array<string>;
  type: PanelSingleType;
  origin?: string;
  destination?: string;
  preventBottomDisplacement?: boolean;
}

export function buildRoom(
  size: number,
  defaultSelection?: { count: number[]; ages: number[][] }
): RoomInfo {
  if (defaultSelection) {
    return {
      key: nanoid(),
      ...defaultSelection
    };
  }
  let count = Array(size);
  count.fill(0);
  count[0] = 1;

  let ages = Array(size);
  ages.fill([]);

  return {
    key: nanoid(),
    count,
    ages
  };
}

export function RoomsSelector(props: Props) {
  const translate = useTranslation();
  const plural = usePlural();
  const totalRooms = props.rooms.totalRooms;
  const totalTravelers = props.rooms.totalTravelers;
  const [focusedElement, setFocusedElement] = useState(false);
  const [id] = React.useState(nanoid());
  const [savedRooms, setSavedRooms] = useState(props.defaultValue?.rooms ?? []);
  const [travelClass, setTravelClass] = useState<string>(props.defaultValue?.travelClass ?? '');
  const contentScrollRef = React.useRef<any>(null);
  const addRoomButtonRef = React.useRef<any>(null);

  const filterContentRef = React.useRef<HTMLDivElement>(null);

  const { rooms, dispatch, isEventValid } = useRooms(
    props.rooms,
    savedRooms,
    (value) =>
      props.onChange?.({
        rooms: value,
        travelClass: travelClass
      }),
    props.passengersRules,
    contentScrollRef
  );
  const isMobile = useIsMobileView();
  const dialog = useMobileFilter({
    onDismiss: () => {
      dispatch({ type: 'SET_ROOMS', value: savedRooms });
    },
    onSave: () => {
      setSavedRooms(rooms);
    }
  });

  const popup = usePopupFilter();
  const showTravelClass =
    props.type.includes('flight') && (props.travelClassConfig?.enabled ?? true);

  const NO_VALUE = -1;
  const SHOW_TRUE_RULE = 0;
  const NO_SHOW_NO_TRUE_RULE = 1;

  function showOption(rule: any, origin: any, destination: any) {
    let modeRule = NO_VALUE;
    const typeRule1 = rule[0];
    const typeRule2 = rule[1];
    const show = rule[2];

    if (typeRule1.length !== 0) {
      let typeRoute = typeRule1[0];
      switch (typeRoute) {
        case 'origin': {
          let countrys = typeRule1[1];
          for (let k = 0; k < countrys.length; k++) {
            if (origin === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            let countrys = typeRule2[1];
            if (countrys !== undefined) {
              for (let k = 0; k < countrys.length; k++) {
                if (destination === countrys[k]) {
                  modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
                  break;
                }
              }
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
        case 'destination': {
          let countrys = typeRule1[1];
          for (let k = 0; k < countrys.length; k++) {
            if (destination === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            let countrys = typeRule2[1];
            if (countrys !== undefined) {
              for (let k = 0; k < countrys.length; k++) {
                if (origin === countrys[k]) {
                  modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
                  break;
                }
              }
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
      }
    } else if (typeRule2.length !== 0) {
      let typeRoute = typeRule2[0];

      switch (typeRoute) {
        case 'origin': {
          let countrys = typeRule2[1];
          for (let k = 0; k < countrys.length; k++) {
            if (origin === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
        case 'destination': {
          let countrys = typeRule2[1];
          for (let k = 0; k < countrys.length; k++) {
            if (destination === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
      }
    }
    return modeRule;
  }

  function applyBusinessRules(
    origin: string | undefined,
    destination: string | undefined,
    options: any | undefined
  ) {
    let newOptions = [];
    let rules;
    let rule;
    let element;
    let modeRule = NO_VALUE;
    for (let i = 0; i < options.length; i++) {
      element = options[i];
      if (element.rules !== undefined && element.rules.length !== 0) {
        rules = element.rules;
        for (let j = 0; j < rules.length; j++) {
          rule = rules[j];
          modeRule = showOption(rule, origin, destination);
        }
        if (modeRule === SHOW_TRUE_RULE) {
          newOptions.push(element);
        }
        modeRule = NO_VALUE;
      } else {
        newOptions.push(element);
      }
    }
    return newOptions;
  }

  const roomsCount = rooms.length;
  const infantIndex = props.rooms.totalTravelersPerRoomExcludeInfants
    ? props.rooms.options.findIndex((item) => item.name === 'age3')
    : -1;
  const guestsCount = count(
    rooms.map((room) =>
      count(
        room.count.filter(
          (itemCount, index) =>
            !props.rooms.totalTravelersPerRoomExcludeInfants || index !== infantIndex
        )
      )
    )
  );

  const guestsLabel = plural({
    count: guestsCount,
    variables: {
      guestsCount
    },
    key: 'cmp-booking-guests-label',
    defaultValue: '{guestsCount} guests'
  });

  const roomsLabel = plural({
    count: roomsCount,
    variables: {
      roomsCount
    },
    key: 'cmp-booking-rooms-label',
    defaultValue: '{roomsCount} rooms'
  });

  const label = translate('cmp-booking-rooms-aria-label', 'Select rooms and guests, ');
  const selectedLabel = translate('cmp-booking-rooms-selected-label');
  const value = translate('cmp-booking-rooms-info-label', '{guestsLabel}, {roomsLabel}', {
    guestsLabel,
    roomsLabel
  });

  const filterButton = (
    <FilterButton
      value={value}
      aria-label={`${label} ${selectedLabel} ${value}`}
      onClick={isMobile ? dialog.open : popup.toggle}
      ref={popup.filterRef}
      active={true}
    />
  );

  const arrayForEnableLegend = props.rooms.enabledLegendIn ?? [];
  const notClose = React.useRef(false);

  const doClosePopup = () => (notClose.current = false);
  const doNotClosePopup = () => (notClose.current = true);

  const main = (
    <Fragment>
      {arrayForEnableLegend.filter((el) => el === props.type).length > 0 ? (
        <Alert
          icon="info"
          labelKey="cmp-room-selector-legend"
          sx={{
            padding: '8px',
            backgroundColor: '#eeeeee',
            ml: 'dialogMargin',
            mr: 'dialogMargin',
            color: 'softText',
            variant: 'layout.legend'
          }}
        />
      ) : null}
      {showTravelClass ? (
        <div sx={{ pl: 'dialogMargin', pr: 'dialogMargin' }}>
          <div id={`screenReaderMessage-${id}`} hidden>
            {translate(`cmp-booking-selected-travel-class-message`, `Selected`)}
          </div>
          <Listbox
            ariaLabelledBy={`screenReaderMessage-${id}`}
            labelNotSelectedItem={translate('cmp-travel-class-label-not-selected-item', 'Elements')}
            label={translate('cmp-travel-class-label', 'Travel Class')}
            layout="standard"
            disabled={props.activateRouteOption && (!props.origin || !props.destination)}
            focusWhenIsShow={false}
            options={applyBusinessRules(
              props.origin,
              props.destination,
              props.travelClasses?.options
            )}
            defaultValue={travelClass}
            onClick={() => doNotClosePopup()}
            onChange={(newValue) => {
              setTravelClass(newValue);
              props.onChange?.({
                rooms: rooms,
                travelClass: newValue
              });
            }}
          />
        </div>
      ) : null}
      <div
        ref={contentScrollRef}
        id="room-container"
        sx={{
          minHeight: '100px',
          overflow: rooms.length > 1 ? 'scroll' : 'visible',
          maxHeight: isMobile ? '100%' : '300px',
          variant: 'layout.roomSelector.scrollContainer'
        }}
      >
        {rooms.map((room, index) => {
          return (
            <div
              key={room.key}
              role="group"
              aria-label={`${translate('cmp-booking-room-label', 'Room')} ${index + 1}`}
            >
              <Stack gap={4} pl="dialogMargin" pr="dialogMargin">
                <Inline align="space-between">
                  <Text variant="text.fontSize4" color="text">
                    {translate('cmp-booking-room-label', 'Room')}
                    {` ${index + 1}`}
                  </Text>
                  {index !== 0 && (
                    <Button
                      id={`remove-room-${index}`}
                      type="button"
                      variant="links.fontSize5"
                      sx={{
                        bg: 'transparent',
                        textDecoration: 'underline',
                        p: '0'
                      }}
                      onClick={() => {
                        doNotClosePopup();
                        dispatch({ type: 'REMOVE_ROOM', roomIndex: index });
                      }}
                      onKeyDown={(event: any) => {
                        // If user press Enter or Space move focus to Add Room
                        if (event.keyCode === KEY_ENTER || event.keyCode === KEY_SPACE) {
                          setTimeout(() => {
                            addRoomButtonRef.current.focus();
                          }, 100);
                        }
                      }}
                    >
                      {translate('cmp-booking-remove-room-button', 'Remove')}
                    </Button>
                  )}
                </Inline>
                <Room
                  origin={props.origin}
                  destination={props.destination}
                  roomIndex={index}
                  options={props.rooms.options}
                  dispatch={dispatch}
                  value={rooms[index]}
                  isEventValid={isEventValid}
                  totalCount={guestsCount}
                  preventBottomDisplacement={props?.preventBottomDisplacement}
                  filterContentRef={filterContentRef}
                  doNotClosePopup={doNotClosePopup}
                />
              </Stack>
              {index < rooms.length - 1 && (
                <Separator sx={{ marginTop: 3, marginBottom: 3 }} aria-hidden />
              )}
            </div>
          );
        })}
      </div>
      {rooms.length >= totalRooms && (
        <VisuallyHidden aria-live="polite" role="alert">
          {translate('cmp-room-selector-maximum-room-reached', 'Maximum number of rooms reached.')}
        </VisuallyHidden>
      )}
    </Fragment>
  );

  const footer = (
    <Inline variant="layout.popover.footer" gap={2} align="end" pb="11px">
      {rooms.length < totalRooms && guestsCount < totalTravelers ? (
        <Button
          ref={addRoomButtonRef}
          type="button"
          variant="link"
          onClick={() => {
            doNotClosePopup();
            dispatch({ type: 'ADD_ROOM' });
          }}
          onKeyDown={(event: any) => {
            // If user press Enter or Space move focus to latest Remove Room
            if (
              (event.keyCode === KEY_ENTER || event.keyCode === KEY_SPACE) &&
              rooms.length + 1 >= totalRooms
            ) {
              setTimeout(() => {
                const lastRemoveRoom = document.getElementById(`remove-room-${rooms.length}`);
                lastRemoveRoom?.focus();
              }, 100);
            }
          }}
        >
          {translate('cmp-booking-add-room-button', 'Add room')}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="link"
        onFocus={() => {
          setFocusedElement(false);
        }}
        onClick={isMobile ? dialog.save : popup.toggle}
        value="Done"
      >
        {translate('cmp-booking-done-room-button', 'Done')}
      </Button>
    </Inline>
  );

  React.useEffect(() => {
    const handleOutsideClick = (e: $TSFixMe) => {
      if (dialog.isOpen || popup.ref.current?.isOpen) {
        if (filterContentRef.current?.contains(e.target) || notClose.current) {
          doClosePopup();
        } else {
          popup.close();
        }
      }
    };

    document.addEventListener('click', handleOutsideClick, false);

    return () => {
      popup.close();
      document.removeEventListener('click', handleOutsideClick, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isMobile) {
    return (
      <React.Fragment>
        {filterButton}
        {dialog.isOpen && (
          <MobileFilterDialog label={props.title || 'Select rooms'}>
            <MobileHeader
              title={props.title || translate('cmp-booking-room-selector-header')}
              onBackButtonPress={dialog.dismiss}
            />
            <MobileBody>
              <Stack
                ref={filterContentRef}
                gap={4}
                pt="30px"
                pb="15px"
                sx={{ variant: 'layout.roomSelector.mobileContainer' }}
              >
                {main}
              </Stack>
            </MobileBody>
            <MobileFooter align="end">{footer}</MobileFooter>
          </MobileFilterDialog>
        )}
      </React.Fragment>
    );
  }
  return (
    <Dropdown
      variant="layout.popover"
      ref={popup.ref}
      targetRef={popup.filterRef}
      toggleElement={filterButton}
      onClose={() => {
        dialog.save();
        if (!focusedElement) {
          popup.filterRef.current.focus();
          setFocusedElement(true);
        }
      }}
    >
      <Stack
        gap={4}
        pt="30px"
        sx={{ variant: 'layout.roomSelector.dropdown' }}
        ref={filterContentRef}
      >
        {main}
        {footer}
      </Stack>
    </Dropdown>
  );
}

interface RoomProps {
  options: PassengersProps['options'];
  roomIndex: number;
  value: PeopleInfo;
  dispatch: ReturnType<typeof useRooms>['dispatch'];
  origin?: string;
  destination?: string;
  isEventValid: ReturnType<typeof useRooms>['isEventValid'];
  totalCount: number;
  preventBottomDisplacement?: boolean;
  filterContentRef?: React.RefObject<HTMLDivElement>;
  doNotClosePopup?: () => void;
}

function Room(props: RoomProps) {
  const dispatch = props.dispatch;
  const roomIndex = props.roomIndex;
  const passengerDispatch = React.useCallback(
    (args: any) => {
      dispatch({ ...args, roomIndex: roomIndex });
    },
    [dispatch, roomIndex]
  );
  return (
    <PeopleCounter
      origin={props.origin}
      destination={props.destination}
      options={props.options}
      layout="inline"
      dispatch={passengerDispatch}
      value={props.value}
      isEventValid={(event, state) => props.isEventValid(event, state, props.totalCount)}
      preventBottomDisplacement={props?.preventBottomDisplacement}
      filterContentRef={props.filterContentRef}
      doNotClosePopup={props.doNotClosePopup}
    />
  );
}

export function useRooms(
  roomProps: Omit<RoomsProps, 'defaultSelection'>,
  defaultState?: RoomInfo[],
  onChange?: (value: RoomInfo[]) => void,
  rules: Array<string> = [],
  contentScrollRef?: any
) {
  const maxCount = roomProps.options.map((v) => v.maxCount);
  const minCount = roomProps.options.map((v) => v.minCount ?? 0);
  const guestsTypesCount = roomProps.options.length;
  const initialState = defaultState ?? [buildRoom(guestsTypesCount)];
  const totalTravelers = roomProps.totalTravelers ?? 1000;
  const totalTravelersPerRoom = roomProps.totalTravelersPerRoom ?? 1000;
  const excludeChildren = roomProps.totalTravelersPerRoomExcludeInfants;

  const isEventValid = (
    event: any,
    room: Omit<RoomInfo, 'key'>,
    totalCount: number,
    isGlobal: boolean = false
  ) => {
    const { valid } = evaluator(room, event.type, event.index, isGlobal);
    switch (event.type) {
      case 'ADD': {
        const roomCount = count(room.count.filter((item, index) => index !== infantIndex)); // : count(room.count);
        const passengerIndex = event.index;
        //Bypass when children and exclude flag is true
        const excludeCondition =
          (excludeChildren && passengerIndex === infantIndex) || isGlobal
            ? true
            : roomCount < totalTravelersPerRoom;
        //To allow add children when total of persons is complete
        const canAddChild =
          excludeChildren && infantIndex === event.index ? true : totalCount < totalTravelers;

        return (
          valid &&
          excludeCondition &&
          canAddChild &&
          (isGlobal ||
            maxCount[passengerIndex] === undefined ||
            room.count[passengerIndex] < (maxCount[passengerIndex] as number))
        );
      }
      case 'REMOVE': {
        const passengerIndex = event.index;
        return room.count[passengerIndex] > minCount[passengerIndex];
      }
      default: {
        return true;
      }
    }
  };

  const getGlobalRoom = (draft: RoomInfo[]) => {
    let roomGlobal: RoomInfo = {
      key: 'global-room',
      count: [0, 0, 0, 0],
      ages: [[], [], [], []]
    };
    draft.forEach((room) => {
      room.count.forEach((ct, i) => (roomGlobal.count[i] += ct));
      room.ages.forEach((age, i) => age.forEach((child) => roomGlobal.ages[i].push(child)));
    });

    return roomGlobal;
  };

  const evaluator = evaluateRules(rules, roomProps);
  //the value to get Infant index is hardcoded because not should change in a future
  const infantIndex = roomProps?.totalTravelersPerRoomExcludeInfants
    ? roomProps.options.findIndex((item) => item.name === 'age3')
    : -1;

  const initialGlobal = getGlobalRoom(initialState);

  const [{ rooms, global }, dispatch] = useEffectReducer(
    (state, event, exec) => {
      const roomGlobal = getGlobalRoom(state.rooms);
      // const roomGlobal = state.global;
      const newState = produce(state.rooms, (draft) => {
        const totalCount = count(
          state.rooms.map((room) => {
            return count(room.count.filter((itemCount, index) => index !== infantIndex));
          })
        );

        const { ruleActions } = evaluator(draft[event.roomIndex], event.type, event.index);
        switch (event.type) {
          case 'ADD': {
            const roomIndex = event.roomIndex;
            const room = draft[roomIndex];
            const passengerIndex = event.index;
            if (
              isEventValid(event, room, totalCount) &&
              isEventValid(event, roomGlobal, totalCount, true)
            ) {
              room.count[passengerIndex] += 1;
              roomGlobal.count[passengerIndex] += 1;
              room.ages[passengerIndex].push(roomProps.options[passengerIndex].minAge);
            }
            break;
          }
          case 'REMOVE': {
            const roomIndex = event.roomIndex;
            const room = draft[roomIndex];
            const passengerIndex = event.index;
            if (isEventValid(event, room, totalCount)) {
              room.count[passengerIndex] -= 1;
              roomGlobal.count[passengerIndex] -= 1;
              room.ages[passengerIndex].pop();
              ruleActions(room);
            }
            break;
          }
          case 'ADD_ROOM': {
            if (totalCount < totalTravelers) {
              draft.push(buildRoom(guestsTypesCount));
              roomGlobal.count[0] += 1;
              setTimeout(() => {
                contentScrollRef.current.scroll({
                  top: 1000,
                  left: 0,
                  behavior: 'smooth'
                });
              }, 500);
            }
            break;
          }
          case 'REMOVE_ROOM': {
            const roomIndex = event.roomIndex;
            const deletedRoom = draft.splice(roomIndex, 1);
            roomGlobal.count[0] -= deletedRoom[0].count[0] ?? 0;
            roomGlobal.count[1] -= deletedRoom[0].count[1] ?? 0;
            roomGlobal.count[2] -= deletedRoom[0].count[2] ?? 0;
            roomGlobal.count[3] -= deletedRoom[0].count[3] ?? 0;
            break;
          }
          case 'SET_ROOMS': {
            return event.value;
          }
          case 'AGE_CHANGE': {
            const roomIndex = event.roomIndex;
            const room = draft[roomIndex];
            const typeIndex = event.typeIndex;
            const instanceIndex = event.instanceIndex;
            room.ages[typeIndex][instanceIndex] = event.value;
            break;
          }
          case 'VALUE_SET': {
            const roomIndex = event.roomIndex;
            const room = draft[roomIndex];
            room.ages = event.value.ages;
            room.count = event.value.count;
          }
        }
      });
      exec(() => onChange?.(newState));
      return { rooms: newState, global: roomGlobal };
    },
    { rooms: initialState, global: initialGlobal }
  );

  return {
    rooms,
    dispatch,
    isEventValid: (evt: any, room: Omit<RoomInfo, 'key'>, total: number) =>
      isEventValid(evt, room, total) && isEventValid(evt, global, total, true)
  };
}
